<!--app\javascript\dashboard\routes\dashboard\bulkmessage\components\BroadcastProgressOverlay.vue-->

<script setup>
import { computed, watch, onUnmounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  totalRecipients: {
    type: Number,
    default: 0,
  },
  currentProgress: {
    type: Number,
    default: 0,
  },
  successCount: {
    type: Number,
    default: 0,
  },
  failedCount: {
    type: Number,
    default: 0,
  },
  errors: {
    type: Array,
    default: () => [],
  },
  broadcastId: {
    type: [String, Number],
    default: null,
  },
  isCancellable: {
    type: Boolean,
    default: true,
  },
  currentBatch: {
    type: Number,
    default: 0,
  },
  totalBatches: {
    type: Number,
    default: 0,
  },
  isProcessingBatch: {
    type: Boolean,
    default: false,
  },
  isCancelling: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancel', 'close']);

// Computed properties
const progressPercentage = computed(() => {
  if (props.totalRecipients === 0) return 0;
  return Math.min(
    100,
    Math.round((props.currentProgress / props.totalRecipients) * 100)
  );
});

const isComplete = computed(() => {
  return props.currentProgress >= props.totalRecipients;
});

const statusText = computed(() => {
  if (props.isCancelling) {
    return 'Cancelando disparo...';
  }
  if (isComplete.value) {
    if (props.failedCount > 0) {
      return 'Disparo finalizado com erros';
    }
    return 'Disparo concluído com sucesso!';
  }
  return 'Enviando mensagens...';
});

const statusClass = computed(() => {
  if (props.isCancelling) {
    return 'text-n-ruby-11 dark:text-red-400';
  }
  if (isComplete.value) {
    if (props.failedCount > 0) {
      return 'text-n-amber-11 dark:text-amber-700';
    }
    return 'text-n-teal-11 dark:text-green-400';
  }
  return 'text-n-blue-11 dark:text-blue-400';
});

const statusIcon = computed(() => {
  if (props.isCancelling) {
    return 'i-lucide-x-circle';
  }
  if (isComplete.value) {
    if (props.failedCount > 0) {
      return 'i-lucide-alert-triangle';
    }
    return 'i-lucide-check-circle';
  }
  return 'i-lucide-loader-2';
});

// Texto que mostra o progresso atual e estimativa de tempo
const progressText = computed(() => {
  if (props.totalRecipients === 0) return '';

  const percent = progressPercentage.value;
  const current = props.currentProgress;
  const total = props.totalRecipients;

  let text = `${current} de ${total} contatos (${percent}%)`;

  return text;
});

// Computed for progress bar color
const progressBarColor = computed(() => {
  if (props.isCancelling) {
    return '#dc2626'; // Red color for cancelling
  }
  if (isComplete.value) {
    if (props.failedCount > 0) {
      return '#f59e0b'; // Amber color
    }
    return '#10b981'; // Green color
  }
  return '#3b82f6'; // Blue color
});

// Methods
const handleCancel = () => {
  emit('cancel');
};

const handleClose = () => {
  // Don't allow closing during cancellation or while sending
  if (props.isCancelling || !isComplete.value) {
    return;
  }
  // Emit close event with the broadcastId for parent components
  emit('close', props.broadcastId);
};

const openDashboardInNewTab = () => {
  // Get current account ID from route or URL
  const currentPath = window.location.pathname;
  const accountMatch = currentPath.match(/\/accounts\/(\d+)/);
  const accountId = accountMatch ? accountMatch[1] : '';

  // Open dashboard in new tab
  const dashboardUrl = accountId
    ? `/app/accounts/${accountId}/dashboard`
    : '/app/dashboard';

  window.open(dashboardUrl, '_blank');
};

// Handle page unload warning
const handleBeforeUnload = e => {
  // Only show warning if sending is in progress (not complete and not just invisible)
  if (props.isVisible && !isComplete.value && !props.isCancelling) {
    // Cancel the event
    e.preventDefault();
    // Chrome requires returnValue to be set
    e.returnValue =
      'O disparo de mensagens está em andamento. Se você sair agora, o envio será interrompido. Tem certeza que deseja sair?';
    return e.returnValue;
  }
};

// Watch for visibility and completion status to add/remove event listener
watch(
  () => props.isVisible && !isComplete.value,
  shouldWarn => {
    if (shouldWarn) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  },
  { immediate: true }
);

// Clean up event listener when component is unmounted
onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/75 dark:bg-n-slate-1/75 backdrop-blur-sm"
    @click.self="isComplete ? handleClose() : null"
  >
    <div
      class="bg-white dark:bg-n-slate-2 rounded-lg shadow-xl w-full max-w-md mx-4"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6">
        <div class="flex items-center">
          <span
            :class="[
              statusIcon,
              statusClass,
              'mr-2 text-xl',
              !isComplete && !isCancelling && 'animate-spin'
            ]"
          ></span>
          <h3 class="text-xl font-semibold" :class="statusClass">
            {{ statusText }}
          </h3>
        </div>
      </div>

      <!-- Body -->
      <div class="px-6 py-4">
        <!-- Progress info -->
        <div class="mb-4">
          <div class="flex justify-between items-center mb-1">
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11">Progresso: {{ progressPercentage }}%</span>
            <span class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">{{ progressText }}</span>
          </div>

          <!-- Progress bar container with responsive background -->
          <div
            class="w-full rounded-full h-3 overflow-hidden bg-n-slate-3 dark:bg-n-slate-6"
          >
            <!-- Progress bar with inline style for color and longer animation duration -->
            <div
              class="h-3 rounded-full transition-all ease-out"
              :class="{
                'duration-1000': isProcessingBatch,
                'duration-300': !isProcessingBatch,
              }"
              :style="{
                width: `${Math.max(progressPercentage, 0.5)}%`,
                backgroundColor: progressBarColor,
              }"
            />
          </div>
        </div>

        <!-- Cancellation explanation (only shown when cancelling) -->
        <div v-if="isCancelling" class="mb-4">
          <div
            class="bg-n-ruby-1 dark:bg-n-ruby-2 border border-n-ruby-4 dark:border-n-ruby-6 rounded-lg p-4"
          >
            <div class="flex items-start">
              <i
                class="i-lucide-info text-n-ruby-11 dark:text-n-ruby-11 text-xl mr-3 mt-0.5"
              />
              <div>
                <h4 class="font-medium text-n-ruby-11 dark:text-n-ruby-11 mb-2">
                  Cancelamento em Andamento
                </h4>
                <p class="text-sm text-n-ruby-11 dark:text-n-ruby-11 mb-2">
                  O sistema está cancelando o disparo. Por favor, aguarde.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Statistics -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="bg-n-slate-1 dark:bg-n-slate-3 rounded-lg p-3">
            <div class="text-sm text-n-slate-11 dark:text-n-slate-11 mb-1">
              Enviados
            </div>
            <div class="text-lg font-medium text-n-grass-11 dark:text-n-grass-11">
              {{ successCount }}
            </div>
          </div>
          <div class="bg-n-slate-1 dark:bg-n-slate-3 rounded-lg p-3">
            <div class="text-sm text-n-slate-11 dark:text-n-slate-11 mb-1">
              Falhas
            </div>
            <div class="text-lg font-medium text-n-ruby-11 dark:text-n-ruby-11">
              {{ failedCount }}
            </div>
          </div>
        </div>

        <!-- Warning message when sending (not complete and not cancelling) -->
        <div v-if="!isComplete && !isCancelling" class="mb-4">
          <div
            class="bg-n-amber-1 dark:bg-n-amber-2 border border-n-amber-4 dark:border-n-amber-6 rounded-lg p-4"
          >
            <div class="flex items-start">
              <i
                class="i-lucide-alert-triangle text-n-amber-11 dark:text-n-amber-11 text-xl mr-3 mt-0.5"
              />
              <div class="flex-1">
                <h4
                  class="font-medium text-n-amber-11 dark:text-n-amber-11 mb-2"
                >
                  Importante - Não feche esta página
                </h4>
                <p class="text-sm text-n-amber-11 dark:text-n-amber-11 mb-3">
                  O disparo será interrompido se você fechar esta página ou
                  navegar para outro lugar. Para continuar usando o sistema, use
                  o botão abaixo para abrir o dashboard em uma nova aba.
                </p>

                <!-- Continue in Dashboard button -->
                <button
                  class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-n-amber-12 dark:text-n-amber-12 bg-n-amber-3 dark:bg-n-amber-4 border border-n-amber-6 dark:border-n-amber-8 rounded-lg hover:bg-n-amber-4 dark:hover:bg-n-amber-5 transition-colors"
                  @click="openDashboardInNewTab"
                >
                  <i class="i-lucide-external-link text-sm" />
                  Continuar no Dashboard (nova aba)
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Error list -->
        <div v-if="errors.length > 0" class="mb-4">
          <div
            class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 mb-2"
          >
            Erros encontrados:
          </div>
          <div
            class="rounded-lg p-2 max-h-40 overflow-y-auto bg-n-ruby-1 dark:bg-n-ruby-2 border border-n-ruby-4 dark:border-n-ruby-6"
          >
            <ul
              class="list-disc list-inside text-sm space-y-1 text-n-ruby-11 dark:text-n-ruby-11"
            >
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-n-slate-3 dark:border-n-slate-6 flex justify-between"
      >
        <!-- Cancelar button (only if not complete and is cancellable) -->
        <Button
          v-if="!isComplete && isCancellable"
          :label="isCancelling ? 'Cancelando...' : 'Cancelar'"
          variant="outline"
          color="ruby"
          :is-loading="isCancelling"
          :disabled="isCancelling"
          @click="handleCancel"
        />
        <!-- Spacer when cancel button isn't shown -->
        <div v-else />

        <!-- Close button (only if complete) -->
        <Button
          v-if="isComplete"
          label="Fechar"
          color="blue"
          @click="handleClose"
        />
        <!-- If not complete, show a disabled "Enviando..." button -->
        <Button
          v-else
          :label="isCancelling ? 'Cancelando...' : 'Enviando...'"
          color="blue"
          :is-loading="true"
          disabled
        />
      </div>
    </div>
  </div>
</template>

<style>
/* Add slow pulse animation for a more subtle effect */
@keyframes pulse-slow {
  0% {
    opacity: 0.9;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.9;
  }
}

.animate-pulse-slow {
  animation: pulse-slow 3s ease-in-out infinite;
}

/* Smooth progress bar transitions */
.progress-bar {
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-bar.batch-processing {
  transition: width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
</style>
